import { MatterInformationClientSection } from "@/components/features/matter/information/MatterInformationClientSection";
import { MatterInformationDefendantSection } from "@/components/features/matter/information/MatterInformationDefendantSection";
import { MatterInformationOtherPartiesSection } from "@/components/features/matter/information/MatterInformationOtherPartiesSection";
import {
  Badge,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Text,
} from "@/components/ui";
import { cn } from "@/lib/utils/cn";
import {
  getlabel,
  getMatterContactConnections,
  getOtherPartyMatterContactConnections,
  isClientPartyConnectionLabel,
  isDefendantPartyConnectionLabel,
} from "@/lib/utils/matters/matter-contact-connection-party-roles";

import { type Matter } from "@/lib/types";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";

const MATTER_DETAILS_TABS = ["client", "defendant", "other-parties"] as const;

function isMatterDetailsTab(t: string) {
  return (MATTER_DETAILS_TABS as readonly string[]).includes(t);
}

export default function MatterInformation({
  matter,
  tab,
}: {
  matter: Matter;
  tab: string;
}) {
  const navigate = useNavigate();
  const { lostWageId, statuteOfLimitationsId, surveyId } = useSearch({
    from: "/_auth/matters/$matterId/_layout/matter-details/$tab",
  });

  const [activeTab, setActiveTab] = useState(() =>
    isMatterDetailsTab(tab) ? tab : "client",
  );
  useEffect(() => {
    if (isMatterDetailsTab(tab)) {
      setActiveTab(tab);
    }
  }, [tab]);

  const clientCount = useMemo(
    () =>
      getMatterContactConnections(matter).filter((connection) =>
        isClientPartyConnectionLabel(getlabel(connection)),
      ).length,
    [matter],
  );

  const defendantCount = useMemo(
    () =>
      getMatterContactConnections(matter).filter((connection) =>
        isDefendantPartyConnectionLabel(getlabel(connection)),
      ).length,
    [matter],
  );

  const otherPartyCount = useMemo(
    () => getOtherPartyMatterContactConnections(matter).length,
    [matter],
  );

  return (
    <div className="bg-background grid min-h-full w-full grid-cols-1 gap-4 px-6 pt-5 pb-8">
      <>
        <Tabs value={activeTab}>
          <TabsList className="mb-2 h-auto w-full justify-start gap-8 rounded-none border-b bg-transparent p-0">
            <TabsTrigger
              onClick={() => {
                setActiveTab("client");
                void navigate({
                  to: `/matters/${matter.id}/matter-details/client`,
                });
              }}
              className="rounded-none px-0 pb-4 text-base data-[state=active]:bg-transparent"
              value="client"
            >
              <div className="flex items-center gap-2">
                <Text
                  as="span"
                  variant={activeTab === "client" ? "body" : "subHeading"}
                  className={cn(
                    activeTab === "client" && "text-foreground font-semibold",
                  )}
                >
                  Client
                </Text>
                <Badge variant="secondary" className="h-5 min-w-5 px-1.5">
                  {clientCount}
                </Badge>
              </div>
            </TabsTrigger>
            <TabsTrigger
              onClick={() => {
                setActiveTab("defendant");
                void navigate({
                  to: `/matters/${matter.id}/matter-details/defendant`,
                });
              }}
              className="rounded-none px-0 pb-4 text-base data-[state=active]:bg-transparent"
              value="defendant"
            >
              <div className="flex items-center gap-2">
                <Text
                  as="span"
                  variant={activeTab === "defendant" ? "body" : "subHeading"}
                  className={cn(
                    activeTab === "defendant" &&
                      "text-foreground font-semibold",
                  )}
                >
                  Defendants
                </Text>
                <Badge variant="secondary" className="h-5 min-w-5 px-1.5">
                  {defendantCount}
                </Badge>
              </div>
            </TabsTrigger>
            <TabsTrigger
              onClick={() => {
                setActiveTab("other-parties");
                void navigate({
                  to: `/matters/${matter.id}/matter-details/other-parties`,
                });
              }}
              className="rounded-none px-0 pb-4 text-base data-[state=active]:bg-transparent"
              value="other-parties"
            >
              <div className="flex items-center gap-2">
                <Text
                  as="span"
                  variant={
                    activeTab === "other-parties" ? "body" : "subHeading"
                  }
                  className={cn(
                    activeTab === "other-parties" &&
                      "text-foreground font-semibold",
                  )}
                >
                  Other parties
                </Text>
                <Badge variant="secondary" className="h-5 min-w-5 px-1.5">
                  {otherPartyCount}
                </Badge>
              </div>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="client" forceMount>
            <MatterInformationClientSection
              matter={matter}
              searchLostWageId={lostWageId}
              searchSurveyId={surveyId}
            />
          </TabsContent>
          <TabsContent value="defendant" forceMount>
            <MatterInformationDefendantSection
              matter={matter}
              searchStatuteOfLimitationsId={statuteOfLimitationsId}
            />
          </TabsContent>
          <TabsContent value="other-parties" forceMount>
            <MatterInformationOtherPartiesSection matter={matter} />
          </TabsContent>
        </Tabs>
      </>
    </div>
  );
}
